import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Wrench, Bell, UserCog, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';

const TOUR_KEY = 'niva_tour_v1';

const STEPS = [
  {
    icon: Sparkles,
    color: 'from-cyan-500 to-blue-600',
    title: 'Meet Niva, your property assistant',
    description:
      'Niva is your AI-powered helper for everything related to your home, available 24/7, no hold music, no waiting.',
  },
  {
    icon: Wrench,
    color: 'from-orange-500 to-red-500',
    title: 'Report issues in seconds',
    description:
      'Describe a problem in plain English. Niva collects all the details, files the request, and keeps you updated. No forms needed.',
  },
  {
    icon: Bell,
    color: 'from-violet-500 to-purple-600',
    title: 'Stay in the loop',
    description:
      'Ask "What\'s the status of my request?" anytime. Niva pulls up your active cases instantly and explains what\'s happening.',
  },
  {
    icon: UserCog,
    color: 'from-slate-500 to-gray-600',
    title: 'Manage your account via chat',
    description:
      'Update your phone number, switch notification preferences, or set your preferred contact method. Just tell Niva.',
  },
];

const NivaOnboardingTour = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (user?.userType === 'Tenant' && !localStorage.getItem(TOUR_KEY)) {
      // Small delay so the dashboard has a chance to paint first
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(TOUR_KEY, '1');
    setVisible(false);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      dismiss();
      navigate('/niva');
    }
  };

  if (!visible) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Skip tour"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Gradient header */}
        <div className={`bg-gradient-to-br ${current.color} p-8 flex justify-center`}>
          <div className="h-16 w-16 bg-white/20 rounded-2xl flex items-center justify-center shadow-inner">
            <Icon className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Step counter */}
        <div className="px-6 pt-4">
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
            {step + 1} of {STEPS.length}
          </span>
        </div>

        {/* Content */}
        <div className="px-6 pt-2 pb-4">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{current.title}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{current.description}</p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between">
          {/* Progress dots */}
          <div className="flex gap-1.5 items-center">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? 'w-5 bg-cyan-500' : i < step ? 'w-1.5 bg-cyan-200' : 'w-1.5 bg-gray-200'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 gap-1.5"
          >
            {isLast ? 'Get Started' : 'Next'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NivaOnboardingTour;
